import { useState, useEffect } from 'react';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import type { Colaborador, Departamento, User, UserRole, MenuPermissao } from '../../types';
import { checkUserManagementPermissions } from '../../types';
import FotoUpload from '../common/FotoUpload';

// Módulos configuráveis no menu
const MENU_MODULES: { id: string; label: string; grupo: string; suportaEscopoFluxo: boolean }[] = [
  { id: 'automacoes', label: 'Automações', grupo: 'Configurações', suportaEscopoFluxo: true },
  { id: 'emails', label: 'E-mails', grupo: 'Configurações', suportaEscopoFluxo: false },
  { id: 'whatsapp', label: 'WhatsApp', grupo: 'Configurações', suportaEscopoFluxo: false },
  { id: 'integracoes', label: 'Integrações', grupo: 'Configurações', suportaEscopoFluxo: false },
  { id: 'crm', label: 'CRM', grupo: 'CRM', suportaEscopoFluxo: false },
  { id: 'dashboards', label: 'Dashboards', grupo: 'Geral', suportaEscopoFluxo: true },
  { id: 'tasks', label: 'Tarefas', grupo: 'Geral', suportaEscopoFluxo: false },
  { id: 'chat', label: 'Chat', grupo: 'Geral', suportaEscopoFluxo: false },
  { id: 'organizacoes', label: 'Organizações', grupo: 'Organização', suportaEscopoFluxo: false },
];

interface ColaboradorAuthModalProps {
  colaborador: Colaborador | null;
  departamentos: Departamento[];
  colaboradores: Colaborador[];
  flows?: Array<{ id: string; nome?: string; name?: string }>; // lista de fluxos para seleção de escopo
  onSave: (colaborador: Omit<Colaborador, 'id' | 'createdAt' | 'updatedAt'> | Colaborador) => void;
  onSaveAuth: (userId: string, userData: { 
    senha?: string;
    role: UserRole;
    status: 'ativo' | 'inativo';
    permissoes?: { menus?: MenuPermissao[] };
  }) => void;
  onClose: () => void;
  existingUsers: User[];
  empresaId: string; // Adicionada propriedade empresaId 
  userPermissions?: {
    canEdit: boolean;
    canAdmin: boolean;
    currentUser?: User;
  };
}

const ColaboradorModal = ({
  colaborador,
  departamentos,
  colaboradores,
  flows = [],
  onSave,
  onSaveAuth,
  onClose,
  existingUsers,
  empresaId,
  userPermissions
}: ColaboradorAuthModalProps) => {
  const [form, setForm] = useState({
    nome: '',
    email: '',
    telefone: '',
    cargo: '',
    departamentoId: '',
    superiorId: '',
    dataContratacao: '',
    status: 'ativo' as 'ativo' | 'inativo',
    isGestor: false,
    foto: '',
    empresaId: empresaId // Inicializar com o empresaId fornecido
  });

  const [authForm, setAuthForm] = useState({
    senha: '',
    confirmSenha: '',
    role: 'viewer' as UserRole,
    createUserAccount: false
  });

  const [errors, setErrors] = useState({
    senha: '',
    confirmSenha: '',
    email: ''
  });

  // Estado dos toggles "olhinho" para mostrar/ocultar senha
  const [showSenha, setShowSenha] = useState(false);
  const [showConfirmSenha, setShowConfirmSenha] = useState(false);
  const [showSenhaAlt, setShowSenhaAlt] = useState(false);
  const [showConfirmSenhaAlt, setShowConfirmSenhaAlt] = useState(false);

  // Permissões de menus
  const [menuPermissoes, setMenuPermissoes] = useState<MenuPermissao[]>([]);

  // Verificar se o colaborador já tem uma conta de usuário
  const [hasUserAccount, setHasUserAccount] = useState(false);
  const [existingUserId, setExistingUserId] = useState<string | null>(null);

  // Lista de possíveis superiores incluindo gestores de outros departamentos
  const getSuperioresDisponiveis = () => {
    if (!form.departamentoId) return [];
    return colaboradores.filter(col => 
      (col.isGestor || col.departamentoId === form.departamentoId) && 
      col.id !== colaborador?.id && 
      col.status === 'ativo'
    );
  };

  useEffect(() => {
    if (colaborador) {
      setForm({
        nome: colaborador.nome,
        email: colaborador.email,
        telefone: (colaborador as any).telefone || '',
        cargo: colaborador.cargo,
        departamentoId: colaborador.departamentoId,
        superiorId: colaborador.superiorId || '',
        dataContratacao: new Date(colaborador.dataContratacao).toISOString().split('T')[0],
        status: colaborador.status,
        isGestor: colaborador.isGestor || false,
        foto: (colaborador as any).foto || '',
        empresaId: colaborador.empresaId || empresaId // Usar o empresaId do colaborador ou o fornecido
      });

      // Verificar se já existe uma conta de usuário com este email
      const existingUser = existingUsers.find(user => user.email === colaborador.email);
      setHasUserAccount(!!existingUser);
      if (existingUser) {
        setExistingUserId(existingUser.id);
        // Normalizar role: 'admin'/'company_admin'/'global_admin' → 'administrator'
        const normalizedRole = ['admin', 'company_admin', 'global_admin'].includes(existingUser.role)
          ? 'administrator' as UserRole
          : existingUser.role;
        setAuthForm(prev => ({
          ...prev,
          role: normalizedRole
        }));
        // Carregar permissões de menus existentes
        setMenuPermissoes((existingUser as any).permissoes?.menus || []);
      } else {
        setMenuPermissoes([]);
      }
    } else {
      // Atualiza o form para garantir que o empresaId esteja sempre atualizado
      setForm(prev => ({
        ...prev,
        empresaId
      }));
    }
  }, [colaborador, existingUsers, empresaId]);
  const validatePassword = () => {
    let valid = true;
    const newErrors = { ...errors };

    // Validar senha para conta nova ou para alteração de conta existente (se senha foi preenchida)
    const shouldValidatePassword = authForm.createUserAccount || (hasUserAccount && authForm.senha);

    if (shouldValidatePassword && authForm.senha && authForm.senha.length < 6) {
      newErrors.senha = 'A senha deve ter pelo menos 6 caracteres';
      valid = false;
    } else {
      newErrors.senha = '';
    }

    if (shouldValidatePassword && authForm.senha && authForm.senha !== authForm.confirmSenha) {
      newErrors.confirmSenha = 'As senhas não coincidem';
      valid = false;
    } else {
      newErrors.confirmSenha = '';
    }

    setErrors(newErrors);
    return valid;
  };

  const validateEmail = () => {
    if (!isValidEmail(form.email)) {
      setErrors(prev => ({ ...prev, email: 'Por favor, insira um email válido' }));
      return false;
    }
    setErrors(prev => ({ ...prev, email: '' }));
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.nome || !form.email || !form.cargo || !form.departamentoId || !form.dataContratacao) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    if (!validateEmail()) {
      return;
    }

    // Validar senha quando criando conta nova OU quando alterando senha de conta existente
    const isChangingExistingPassword = hasUserAccount && !!authForm.senha;
    if ((authForm.createUserAccount || isChangingExistingPassword) && !validatePassword()) {
      return;
    }

    // Certificar-se de que empresaId está incluído
    // ✅ CORREÇÃO: Incluir role nos dados do colaborador para sincronização com banco
    const formWithEmpresaId = {
      ...form,
      empresaId,
      role: authForm.role  // Incluir role para o backend sincronizar
    };

    // Salvar o colaborador
    if (colaborador) {
      onSave({
        ...colaborador,
        ...formWithEmpresaId,
        dataContratacao: new Date(form.dataContratacao)
      });
    } else {
      // Colaborador novo: a senha e as permissoes vao no MESMO request. O backend
      // cria o acesso junto. Fazer isso em duas etapas nao funcionava, porque a
      // segunda procurava o colaborador numa lista que ainda nao tinha recarregado.
      onSave({
        ...formWithEmpresaId,
        ...(authForm.createUserAccount && authForm.senha
          ? { senha: authForm.senha, permissoes: { menus: menuPermissoes } }
          : {}),
        dataContratacao: new Date(form.dataContratacao)
      });
    }

    // So para colaborador JA existente (trocar senha/role/permissoes do acesso).
    if (colaborador && (authForm.createUserAccount || hasUserAccount)) {
      const userId = existingUserId || form.email; // Use o ID existente ou o email como ID
      
      // ✅ CORRIGIDO: Só atualizar senha se foi preenchida
      const userDataToUpdate: any = {
        role: authForm.role,
        status: form.status,
        permissoes: { menus: menuPermissoes }
      };
      
      // Incluir senha APENAS se foi preenchida (nova conta ou alteração de senha)
      if (authForm.senha && authForm.senha.trim() !== '') {
        userDataToUpdate.senha = authForm.senha;
      }
      
      onSaveAuth(userId, userDataToUpdate);
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Quando o departamento muda, limpa o superior selecionado se não for gestor
  const handleDepartamentoChange = (departamentoId: string) => {
    setForm(prev => {
      const newForm = {
        ...prev,
        departamentoId
      };
      
      // Se o superior atual não é gestor e está em outro departamento, limpa a seleção
      const currentSuperior = colaboradores.find(c => c.id === prev.superiorId);
      if (currentSuperior && !currentSuperior.isGestor && currentSuperior.departamentoId !== departamentoId) {
        newForm.superiorId = '';
      }
      
      return newForm;
    });
  };

  const superioresDisponiveis = getSuperioresDisponiveis();
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {colaborador ? 'Editar Colaborador' : 'Novo Colaborador'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div><form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* === Dados do Colaborador === */}
          <div className="space-y-4">
            {/* Foto do Colaborador */}
            <FotoUpload 
              foto={form.foto}
              nome={form.nome}
              onChange={(foto) => setForm({ ...form, foto })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome completo *
              </label>
              <input
                type="text"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Digite o nome do colaborador"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email corporativo *
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="email@empresa.com"
                  required
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <input
                  type="tel"
                  value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cargo *
                </label>
                <input
                  type="text"
                  value={form.cargo}
                  onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Ex: Analista de RH"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Departamento *
              </label>
              <select
                value={form.departamentoId}
                onChange={(e) => handleDepartamentoChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              >
                <option value="">Selecione um departamento</option>
                {departamentos.map((dep) => (
                  <option key={dep.id} value={dep.id}>
                    {dep.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Campo É Gestor */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isGestor}
                onChange={(e) => setForm({ ...form, isGestor: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                id="isGestor"
              />
              <label htmlFor="isGestor" className="text-sm font-medium text-gray-700">
                É Gestor
              </label>
              {form.isGestor && (
                <span className="text-xs text-gray-500 ml-2">
                  (Pode ser superior de colaboradores de outros departamentos)
                </span>
              )}
            </div>            {form.departamentoId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Superior Imediato
                </label>
                <select
                  value={form.superiorId}
                  onChange={(e) => setForm({ ...form, superiorId: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Sem superior direto</option>
                  {superioresDisponiveis.map((superior) => (
                    <option key={superior.id} value={superior.id}>
                      {superior.nome} - {superior.cargo}
                      {superior.departamentoId !== form.departamentoId ? ' (Gestor)' : ''}
                    </option>
                  ))}
                </select>
                {superioresDisponiveis.length === 0 && form.departamentoId && (
                  <p className="mt-1 text-sm text-gray-500">
                    Não há outros colaboradores disponíveis para definir como superior.
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Contratação *
                </label>
                <input
                  type="date"
                  value={form.dataContratacao}
                  onChange={(e) => setForm({ ...form, dataContratacao: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <div className="flex gap-4 mt-2">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="ativo"
                      checked={form.status === 'ativo'}
                      onChange={(e) => setForm({ ...form, status: e.target.value as 'ativo' | 'inativo' })}
                      className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-gray-700">Ativo</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="inativo"
                      checked={form.status === 'inativo'}
                      onChange={(e) => setForm({ ...form, status: e.target.value as 'ativo' | 'inativo' })}
                      className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-gray-700">Inativo</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* === Dados de Autenticação === */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Dados de Acesso ao Sistema</h3>
              {!hasUserAccount && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="createUserAccount"
                    checked={authForm.createUserAccount}
                    onChange={(e) => setAuthForm({ ...authForm, createUserAccount: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="createUserAccount" className="ml-2 text-sm text-gray-700">
                    Criar conta de acesso
                  </label>
                </div>
              )}
            </div>

            {hasUserAccount && (
              <div className="p-3 bg-blue-50 text-blue-700 rounded-lg mb-4">
                <p className="text-sm">Este colaborador já possui uma conta de acesso ao sistema.</p>
              </div>
            )}

            {(authForm.createUserAccount || hasUserAccount) && (
              <div className="space-y-4">                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nível de Acesso
                  </label>
                  {userPermissions?.canAdmin ? (
                    <select
                      value={authForm.role}
                      onChange={(e) => setAuthForm({ ...authForm, role: e.target.value as UserRole })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="viewer">Consulta</option>
                      <option value="editor">Editor</option>
                      <option value="administrator">Administrador</option>
                    </select>
                  ) : (
                    <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500">
                      {authForm.role === 'viewer' ? 'Consulta' : 
                       authForm.role === 'editor' ? 'Editor' : 
                       ['administrator', 'admin', 'company_admin', 'global_admin'].includes(authForm.role) ? 'Administrador' : authForm.role}
                    </div>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Consulta: Visualização apenas | Editor: Edição de dados | Administrador: Acesso total
                    {!userPermissions?.canAdmin && <span className="text-amber-600"> (Somente administradores podem alterar)</span>}
                  </p>
                </div>                {!hasUserAccount && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Senha *
                      </label>
                      <div className="relative">
                        <input
                          type={showSenha ? 'text' : 'password'}
                          value={authForm.senha}
                          onChange={(e) => {
                            const v = e.target.value;
                            setAuthForm({ ...authForm, senha: v });
                            // Limpa erro de mismatch ao digitar se voltarem a coincidir
                            if (errors.confirmSenha && v === authForm.confirmSenha) {
                              setErrors(prev => ({ ...prev, confirmSenha: '' }));
                            }
                          }}
                          className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                            errors.senha ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Digite a senha"
                          required={authForm.createUserAccount}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSenha(s => !s)}
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                          tabIndex={-1}
                          title={showSenha ? 'Ocultar senha' : 'Mostrar senha'}
                        >
                          {showSenha ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                      </div>
                      {errors.senha ? (
                        <p className="mt-1 text-sm text-red-600">{errors.senha}</p>
                      ) : (
                        <p className="mt-1 text-xs text-gray-500">
                          A senha deve ter pelo menos 6 caracteres
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirmar Senha *
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmSenha ? 'text' : 'password'}
                          value={authForm.confirmSenha}
                          onChange={(e) => {
                            const v = e.target.value;
                            setAuthForm({ ...authForm, confirmSenha: v });
                            // Validação ao vivo
                            if (authForm.senha && v && authForm.senha !== v) {
                              setErrors(prev => ({ ...prev, confirmSenha: 'As senhas não coincidem' }));
                            } else {
                              setErrors(prev => ({ ...prev, confirmSenha: '' }));
                            }
                          }}
                          className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                            errors.confirmSenha ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Confirme a senha"
                        required={authForm.createUserAccount}
                      />
                        <button
                          type="button"
                          onClick={() => setShowConfirmSenha(s => !s)}
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                          tabIndex={-1}
                          title={showConfirmSenha ? 'Ocultar senha' : 'Mostrar senha'}
                        >
                          {showConfirmSenha ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                      </div>
                      {errors.confirmSenha && (
                        <p className="mt-1 text-sm text-red-600">{errors.confirmSenha}</p>
                      )}
                    </div>
                  </div>
                )}                {hasUserAccount && userPermissions?.canAdmin && (                  <div className="space-y-4">
                    {checkUserManagementPermissions(userPermissions?.currentUser?.role || 'viewer', 'changePassword') ? (
                      <>
                        <div className="p-3 bg-blue-50 text-blue-700 rounded-lg">
                          <p className="text-sm font-medium mb-2">
                            Alterar senha da conta existente
                          </p>
                          <p className="text-xs">
                            Deixe em branco para manter a senha atual.
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nova Senha (opcional)
                            </label>
                            <div className="relative">
                              <input
                                type={showSenhaAlt ? 'text' : 'password'}
                                value={authForm.senha}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setAuthForm({ ...authForm, senha: v });
                                  if (errors.confirmSenha && v === authForm.confirmSenha) {
                                    setErrors(prev => ({ ...prev, confirmSenha: '' }));
                                  }
                                  if (errors.senha && v.length >= 6) {
                                    setErrors(prev => ({ ...prev, senha: '' }));
                                  }
                                }}
                                className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                                  errors.senha ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Digite a nova senha (opcional)"
                              />
                              <button
                                type="button"
                                onClick={() => setShowSenhaAlt(s => !s)}
                                className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                                tabIndex={-1}
                                title={showSenhaAlt ? 'Ocultar senha' : 'Mostrar senha'}
                              >
                                {showSenhaAlt ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                              </button>
                            </div>
                            {errors.senha ? (
                              <p className="mt-1 text-sm text-red-600">{errors.senha}</p>
                            ) : (
                              <p className="mt-1 text-xs text-gray-500">
                                Deixe em branco para manter a senha atual
                              </p>
                            )}
                          </div>

                          {authForm.senha && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Confirmar Nova Senha
                              </label>
                              <div className="relative">
                                <input
                                  type={showConfirmSenhaAlt ? 'text' : 'password'}
                                  value={authForm.confirmSenha}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setAuthForm({ ...authForm, confirmSenha: v });
                                    if (authForm.senha && v && authForm.senha !== v) {
                                      setErrors(prev => ({ ...prev, confirmSenha: 'As senhas não coincidem' }));
                                    } else {
                                      setErrors(prev => ({ ...prev, confirmSenha: '' }));
                                    }
                                  }}
                                  className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                                    errors.confirmSenha ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                  placeholder="Confirme a nova senha"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowConfirmSenhaAlt(s => !s)}
                                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                                  tabIndex={-1}
                                  title={showConfirmSenhaAlt ? 'Ocultar senha' : 'Mostrar senha'}
                                >
                                  {showConfirmSenhaAlt ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                              </div>
                              {errors.confirmSenha ? (
                                <p className="mt-1 text-sm text-red-600">{errors.confirmSenha}</p>
                              ) : (authForm.senha && authForm.confirmSenha && authForm.senha === authForm.confirmSenha && (
                                <p className="mt-1 text-xs text-green-600">✓ As senhas coincidem</p>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="p-3 bg-yellow-50 text-yellow-700 rounded-lg">
                        <p className="text-sm">
                          🔒 Apenas administradores podem alterar senhas de contas existentes.
                        </p>
                      </div>
                    )}
                  </div>
                )}              </div>
            )}
          </div>

          {/* === Permissões de Menus === */}
          {userPermissions?.canAdmin && (hasUserAccount || authForm.createUserAccount) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2">Permissões de Menus</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <p className="text-xs text-gray-500">
                Configure quais módulos este usuário pode acessar. Administradores sempre têm acesso total.
              </p>
              {(() => {
                const grupos = Array.from(new Set(MENU_MODULES.map(m => m.grupo)));
                return grupos.map(grupo => (
                  <div key={grupo} className="space-y-1">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{grupo}</p>
                    {MENU_MODULES.filter(m => m.grupo === grupo).map(modulo => {
                      const perm = menuPermissoes.find(p => p.modulo === modulo.id);
                      const habilitado = !!perm;
                      const toggleModulo = () => {
                        if (habilitado) {
                          setMenuPermissoes(prev => prev.filter(p => p.modulo !== modulo.id));
                        } else {
                          setMenuPermissoes(prev => [...prev, { modulo: modulo.id, nivel: 'visualizar' }]);
                        }
                      };
                      const setNivel = (nivel: 'visualizar' | 'editar') => {
                        setMenuPermissoes(prev => prev.map(p => p.modulo === modulo.id ? { ...p, nivel } : p));
                      };
                      const toggleFluxo = (flowId: string) => {
                        setMenuPermissoes(prev => prev.map(p => {
                          if (p.modulo !== modulo.id) return p;
                          const ids = p.fluxosIds || [];
                          const next = ids.includes(flowId) ? ids.filter(id => id !== flowId) : [...ids, flowId];
                          return { ...p, fluxosIds: next };
                        }));
                      };
                      return (
                        <div key={modulo.id} className={`rounded-lg border p-3 transition-colors ${habilitado ? 'border-indigo-200 bg-indigo-50/50' : 'border-gray-200 bg-white'}`}>
                          <div className="flex items-center justify-between gap-3">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={habilitado}
                                onChange={toggleModulo}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-sm font-medium text-gray-700">{modulo.label}</span>
                            </label>
                            {habilitado && (
                              <div className="flex items-center gap-3">
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`nivel-${modulo.id}`}
                                    value="visualizar"
                                    checked={perm?.nivel === 'visualizar'}
                                    onChange={() => setNivel('visualizar')}
                                    className="h-3.5 w-3.5 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                  />
                                  <span className="text-xs text-gray-600">Visualizar</span>
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`nivel-${modulo.id}`}
                                    value="editar"
                                    checked={perm?.nivel === 'editar'}
                                    onChange={() => setNivel('editar')}
                                    className="h-3.5 w-3.5 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                  />
                                  <span className="text-xs text-gray-600">Editar</span>
                                </label>
                              </div>
                            )}
                          </div>
                          {habilitado && modulo.suportaEscopoFluxo && flows.length > 0 && (
                            <div className="mt-2 ml-6">
                              <p className="text-[11px] text-gray-500 mb-1.5">Escopo por fluxo (vazio = todos):</p>
                              <div className="flex flex-wrap gap-1.5">
                                {flows.map(f => {
                                  const flowName = f.nome || (f as any).name || f.id;
                                  const selected = (perm?.fluxosIds || []).includes(f.id);
                                  return (
                                    <button
                                      key={f.id}
                                      type="button"
                                      onClick={() => toggleFluxo(f.id)}
                                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                                        selected
                                          ? 'bg-indigo-600 text-white border-indigo-600'
                                          : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                                      }`}
                                    >
                                      {flowName}
                                    </button>
                                  );
                                })}
                              </div>
                              {(perm?.fluxosIds || []).length === 0 && (
                                <p className="text-[11px] text-indigo-500 mt-1">Acesso a todos os fluxos</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
          )}

          <div className="pt-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!userPermissions?.canEdit && !!colaborador}
              className={`px-4 py-2 rounded-lg ${
                !userPermissions?.canEdit && !!colaborador
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
              }`}
            >
              {colaborador ? 'Salvar Alterações' : 'Adicionar Colaborador'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ColaboradorModal;